/* eslint-disable no-shadow */
/* eslint-disable camelcase */
/* eslint-disable object-curly-newline */
import React, { useContext, useEffect, useState } from 'react'
import { useHistory } from 'react-router-dom'
import ConsumerActivationPaths from '@sites/consumer/activation/ConsumerActivationPaths'
import { useLocation } from 'react-router'
import Sites from '@sites/index'
import { setPageAnalytics } from '@apps/utils/Analytics'
import { getPromoNames } from '@apps/utils/common'
import { APPLICATION_STATE, ApplicationContext, setActivateAllTrigerred } from '@apps/contexts/ApplicationContext'
import { useDispatch, useSelector } from 'react-redux'
import { getBySlug, getById, getImgByTitle } from '@apps/utils/contentful'
import { navigateToPath } from '@apps/utils/navigation'
import { buildRetryResetPayload } from '@apps/utils/retryReset'
import { updatePaymentStates } from '@apps/redux/features/PayAndReviewSlice'
import { ANALYTICS_EVENT_TYPES } from '@apps/utils/constants'
import ActivationErrorLayout from '@apps/flows/activation/sections/ActivationErrorLayout'
import { Button, Typography } from '@telus/koodo-ui-kit'
import useTagManagerInfo from '../../../_engine/hooks/useTagManagerInfo'

const ProcessFailure = () => {
  // Select only specific slices/fields to avoid subscribing to the entire root state which reduces unnecessary re-renders
  const { content: { pageContent, featureFlags } = {}, responseError, userType, quote: { quoteInfo = {} } = {} } = useSelector((state) => state.common)
  const { plan, localizedPlanItem } = useSelector((state) => state.plansPage)
  const { multiPromotionCodeInfo } = useSelector((state) => state.payAndReviewPage)

  const consumerActivationPaths = new ConsumerActivationPaths()

  const history = useHistory()
  if (featureFlags && featureFlags[0]?.showSplashing) {
    history.push(consumerActivationPaths.Splash)
  }

  const {
    [APPLICATION_STATE.CURRENT_LANGUAGE]: language,
    [APPLICATION_STATE.CURRENT_REGION]: region,
    [APPLICATION_STATE.IDENTITY]: identity
  } = useContext(ApplicationContext)

  const dispatch = useDispatch()
  const updatePaymentStore = (payload) => {
    dispatch(updatePaymentStates(payload))
  }

  const [errorLink, setErrorLink] = useState(false)

  const location = useLocation()

  useEffect(() => {
    setActivateAllTrigerred(false)
  }, [])

  const tagManagerInfo = useTagManagerInfo({
    customFields: {
      error_response_code: responseError?.code || 'NA',
      field_warning: responseError?.message || 'NA',
    }
  })


  const pushClickAnalytics = ({ click_text, event_type, event_click = ANALYTICS_EVENT_TYPES.CLICK } = {}) => {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push(
      setPageAnalytics(location, 'click', region, language, {
        ...tagManagerInfo,
        click_text
      }, event_type, event_click)
    )
  }

  const pushTagManagerToDataLayer = () => {
    const event_type = 'Activation Unsuccessfull'
    window.dataLayer = window.dataLayer || []
    window.dataLayer.push(
      setPageAnalytics(location, 'form', region, language, {
        ...tagManagerInfo,
        promoCodeDescription: getPromoNames([plan?.promo, ...multiPromotionCodeInfo])
      }, event_type)
    )
  }

  useEffect(() => {
    if (userType === Sites.consumer) {
      setTimeout(() => {
        pushTagManagerToDataLayer()
      }, 0)
    }
  }, [region, language])

  useEffect(() => {
    if ((quoteInfo?.quoteState !== 'DELIVERED') && (quoteInfo?.quoteState !== 'SUBMITTED_FOR_DELIVERY') && (quoteInfo?.quoteState !== 'SUBMITTING_FOR_DELIVERY')) {
      setErrorLink(true)
    } else {
      setErrorLink(false)
    }

    // Add event listener for back button. Full arg list matches the Retry
    // click and `ActivationRoutes.processQuoteData` so the 'Payment
    // Information' branch of `navigateToPath` can also detect a
    // now-retired plan and reroute to the plans page when needed.
    const unlisten = history.listen((location, action) => {
      if (action === 'pop' || action === 'POP') {
        if (quoteInfo?.currentState) {
          navigateToPath(
            quoteInfo.currentState,
            consumerActivationPaths,
            history,
            language,
            region,
            localizedPlanItem,
            quoteInfo?.pricePlanId
          )
        }
      }
    })

    const handleBeforeUnload = (event) => {
      event.preventDefault()
      event.returnValue = "Your work will be lost."
    }

    window.addEventListener('beforeunload', handleBeforeUnload)

    // Cleanup function
    return () => {
      unlisten()
      window.removeEventListener('beforeunload', handleBeforeUnload)
    }

  }, [history, location, region, language, quoteInfo?.quoteState])

  useEffect(() => {
    const unblock = history.block((location, action) => {
      if (action === 'pop' || action === 'POP') {
        return false
      }

      return () => {
        unblock()
      }
    })
  }, [history])

  // Same Contentful slug the plans page and PaymentPage use to label the plan's
  // auto promo in the multi-promo arrays.
  // This file is only rendered by the consumer mount, so the page is known
  // — no need to branch on userType, which would silently read the wrong entry
  // if it were ever unset.
  const autoPromoText = getBySlug(
    getById(pageContent, 'plansPageConsumer')?.[language]?.shortTextFields,
    'autoPromoText'
  )?.value

  const activatePage = getById(pageContent, 'transactionSummaryPage')[language]
  const handleNextClick = async () => {
    pushClickAnalytics({ click_text: getBySlug(activatePage?.shortTextFields, 'processing-failure-bottom-message')?.value, event_type: 'Retry button clicked' })
    updatePaymentStore(buildRetryResetPayload({
      plan,
      multiPromotionCodeInfo,
      autoPromoText
    }))

    // Route to wherever the BACKEND says the quote is now, not always
    // Payment. The backend can reset `currentState` to an earlier step
    // between when the user opened Payment and when they clicked Activate
    // — most commonly when their reserved phone number was activated by
    // another customer during the dwell time, dropping the quote back to
    // 'Phone Number Selection'. Sending them to Payment would just fail
    // again with the same "no phone number" error. Full arg list matches
    // `ActivationRoutes.processQuoteData` so the 'Payment Information'
    // branch can also detect a now-retired plan and route to the plans page
    // when needed. If `currentState` is missing (rare — retrieveQuote
    // returned partial data), fall back to Splash, which matches
    // `navigateToPath`'s own default branch for unknown states.
    if (quoteInfo?.currentState) {
      navigateToPath(
        quoteInfo.currentState,
        consumerActivationPaths,
        history,
        language,
        region,
        localizedPlanItem,
        quoteInfo?.pricePlanId
      )
    } else {
      history.push(consumerActivationPaths.Splash)
    }
  }


  return (
    <ActivationErrorLayout
      imageUrl={getImgByTitle(activatePage, 'processing-failure-image')?.url}
      imageAlt={getImgByTitle(activatePage, 'processing-failure-image')?.altText}
      heading={getBySlug(activatePage?.shortTextFields, 'processing-failure-title')?.value}
      body={getBySlug(activatePage?.shortTextFields, 'processing-failure-message')?.value}
      errorCode={responseError?.code && (
        <Typography tokens={{ fontSize: 24, fontWeight: 700, color: '#000000' }} block>
          {getBySlug(activatePage?.shortTextFields, 'processing-failure-error-label')?.value}
          {responseError?.code}
        </Typography>
      )}
    >
      {errorLink ? (
        <div className="w-full sm:w-[260px]">
          <Button
            onPress={() => handleNextClick()}
            variant={{ width: 'full' }}
            tokens={{ backgroundColor: '#000000', color: '#ffffff', borderColor: '#000000' }}
            testId="plans-nextStep"
          >
            {getBySlug(activatePage?.shortTextFields, 'processing-failure-bottom-message')?.value}
          </Button>
        </div>
      ) : null}
    </ActivationErrorLayout>
  )
}

export default ProcessFailure
